import React from 'react';
import { Button, Input, Switch, Tag } from 'antd';

interface ProcessNodePropertyPanelProps {
  nodeId: string;
  parentNodeId: string;
  name: string;
  nameError: string | null;
  codeName: string;
  codeNameError: string | null;
  description: string;
  namespace: string;
  packageName: string;
  packageNameError: string | null;
  packageNameDisabled: boolean;
  tags: string[];
  tagInput: string;
  autonomyAction: string;
  autonomyPrompt: string;
  autonomyWithTool: boolean;
  inputParameters: string[];
  outputParameters: string[];
  processTypeText: string;
  runtimeConfigText: string;
  nameInputRef: React.RefObject<any>;
  codeNameInputRef: React.RefObject<any>;
  descriptionInputRef: React.RefObject<any>;
  packageNameInputRef: React.RefObject<any>;
  onNameChange: (value: string) => void;
  onNameFocus: () => void;
  onNameBlur: (value: string) => void;
  onCodeNameChange: (value: string) => void;
  onCodeNameFocus: () => void;
  onCodeNameBlur: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onDescriptionFocus: () => void;
  onDescriptionBlur: (value: string) => void;
  onPackageNameChange: (value: string) => void;
  onPackageNameFocus: () => void;
  onPackageNameBlur: (value: string) => void;
  onTagInputChange: (value: string) => void;
  onAddTag: () => void;
  onRemoveTag: (tag: string) => void;
  onAutonomyActionChange: (value: string) => void;
  onAutonomyActionFocus: () => void;
  onAutonomyActionBlur: (value: string) => void;
  onAutonomyPromptChange: (value: string) => void;
  onAutonomyPromptFocus: () => void;
  onAutonomyPromptBlur: (value: string) => void;
  onAutonomyWithToolChange: (checked: boolean) => void;
  onOpenProcessDefinition: () => void | Promise<void>;
  renderChipList: (
    items: string[],
    emptyText?: string,
    colorClass?: string
  ) => React.ReactNode;
}

const FieldBlock: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => {
  return (
    <div className="flex flex-col gap-2 text-left">
      <div className="text-xs font-medium text-gray-500">{label}</div>
      <div>{children}</div>
    </div>
  );
};

const ReadonlyTextValue: React.FC<{
  value: string;
}> = ({ value }) => {
  return <div className="text-sm text-gray-700 break-all">{value || '-'}</div>;
};

const DEFAULT_PACKAGE_NAME_PLACEHOLDER = '请输入包名，如 com.example';
const DISABLED_PACKAGE_NAME_PLACEHOLDER = '继承上级包名，不可单独设置';
const DISABLED_PACKAGE_NAME_TITLE = '当前节点继承上级包名，不可单独设置';

const SectionBlock: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => {
  return (
    <section className="flex flex-col gap-4">
      <div className="text-sm font-semibold text-gray-800">{title}</div>
      <div className="flex flex-col gap-4">{children}</div>
    </section>
  );
};

const ProcessNodePropertyPanel: React.FC<ProcessNodePropertyPanelProps> = ({
  nodeId,
  parentNodeId,
  name,
  nameError,
  codeName,
  codeNameError,
  description,
  namespace,
  packageName,
  packageNameError,
  packageNameDisabled,
  tags,
  tagInput,
  autonomyAction,
  autonomyPrompt,
  autonomyWithTool,
  inputParameters,
  outputParameters,
  processTypeText,
  runtimeConfigText,
  nameInputRef,
  codeNameInputRef,
  descriptionInputRef,
  packageNameInputRef,
  onNameChange,
  onNameFocus,
  onNameBlur,
  onCodeNameChange,
  onCodeNameFocus,
  onCodeNameBlur,
  onDescriptionChange,
  onDescriptionFocus,
  onDescriptionBlur,
  onPackageNameChange,
  onPackageNameFocus,
  onPackageNameBlur,
  onTagInputChange,
  onAddTag,
  onRemoveTag,
  onAutonomyActionChange,
  onAutonomyActionFocus,
  onAutonomyActionBlur,
  onAutonomyPromptChange,
  onAutonomyPromptFocus,
  onAutonomyPromptBlur,
  onAutonomyWithToolChange,
  onOpenProcessDefinition,
  renderChipList,
}) => {
  return (
    <div className="flex flex-col gap-6">
      <SectionBlock title="基本信息">
        <div className="flex flex-col gap-2">
          <FieldBlock label="命名空间">
            <ReadonlyTextValue value={namespace} />
          </FieldBlock>

          <FieldBlock label="包名">
            <div className="flex flex-col gap-1">
              <Input
                ref={packageNameInputRef}
                value={packageName}
                placeholder={
                  packageNameDisabled
                    ? DISABLED_PACKAGE_NAME_PLACEHOLDER
                    : DEFAULT_PACKAGE_NAME_PLACEHOLDER
                }
                title={
                  packageNameDisabled ? DISABLED_PACKAGE_NAME_TITLE : undefined
                }
                status={packageNameError ? 'error' : undefined}
                disabled={packageNameDisabled}
                onChange={e => onPackageNameChange(e.target.value)}
                onFocus={onPackageNameFocus}
                onBlur={e => onPackageNameBlur(e.target.value)}
                onPressEnter={e => (e.target as HTMLInputElement).blur()}
              />
              {packageNameError && (
                <div className="text-xs text-red-500">{packageNameError}</div>
              )}
            </div>
          </FieldBlock>

          <FieldBlock label="节点ID">
            <ReadonlyTextValue value={nodeId} />
          </FieldBlock>
        </div>

        <FieldBlock label="过程名称">
          <div className="flex flex-col gap-1">
            <Input
              ref={nameInputRef}
              value={name}
              placeholder="请输入过程名称"
              maxLength={20}
              showCount
              status={nameError ? 'error' : undefined}
              onChange={e => onNameChange(e.target.value)}
              onFocus={onNameFocus}
              onBlur={e => onNameBlur(e.target.value)}
              onPressEnter={e => (e.target as HTMLInputElement).blur()}
            />
            {nameError && (
              <div className="text-xs text-red-500">{nameError}</div>
            )}
          </div>
        </FieldBlock>

        <FieldBlock label="英文名称">
          <div className="flex flex-col gap-1">
            <Input
              ref={codeNameInputRef}
              value={codeName}
              placeholder="请输入英文名称"
              maxLength={200}
              showCount
              status={codeNameError ? 'error' : undefined}
              onChange={e => onCodeNameChange(e.target.value)}
              onFocus={onCodeNameFocus}
              onBlur={e => onCodeNameBlur(e.target.value)}
              onPressEnter={e => (e.target as HTMLInputElement).blur()}
            />
            {codeNameError && (
              <div className="text-xs text-red-500">{codeNameError}</div>
            )}
          </div>
        </FieldBlock>

        <FieldBlock label="描述">
          <Input.TextArea
            ref={descriptionInputRef}
            value={description}
            placeholder="请输入描述（可选）"
            rows={4}
            maxLength={200}
            showCount
            onChange={e => onDescriptionChange(e.target.value)}
            onFocus={onDescriptionFocus}
            onBlur={e => onDescriptionBlur(e.target.value)}
          />
        </FieldBlock>

        <FieldBlock label="标签">
          <div className="flex flex-wrap items-center gap-2">
            {tags.map(tag => (
              <Tag
                key={tag}
                closable
                onClose={() => onRemoveTag(tag)}
                className="!m-0"
              >
                {tag}
              </Tag>
            ))}
            <Input
              size="small"
              value={tagInput}
              placeholder="+NewTag"
              onChange={e => onTagInputChange(e.target.value)}
              maxLength={5}
              onPressEnter={onAddTag}
              onBlur={onAddTag}
              className="!w-24"
            />
          </div>
        </FieldBlock>

        <FieldBlock label="action">
          <Input
            value={autonomyAction}
            placeholder="请输入 action"
            onChange={e => onAutonomyActionChange(e.target.value)}
            onFocus={onAutonomyActionFocus}
            onBlur={e => onAutonomyActionBlur(e.target.value)}
            onPressEnter={e => (e.target as HTMLInputElement).blur()}
          />
        </FieldBlock>

        <FieldBlock label="prompt">
          <Input.TextArea
            value={autonomyPrompt}
            placeholder="请输入 prompt"
            rows={4}
            onChange={e => onAutonomyPromptChange(e.target.value)}
            onFocus={onAutonomyPromptFocus}
            onBlur={e => onAutonomyPromptBlur(e.target.value)}
          />
        </FieldBlock>

        <FieldBlock label="withTool">
          <Switch
            checked={autonomyWithTool}
            onChange={onAutonomyWithToolChange}
          />
        </FieldBlock>
      </SectionBlock>

      <SectionBlock title="其他信息">
        <FieldBlock label="所属父节点ID">
          <ReadonlyTextValue value={parentNodeId} />
        </FieldBlock>
      </SectionBlock>

      <SectionBlock title="Process 定义概览">
        <FieldBlock label="输入参数">
          {renderChipList(
            inputParameters,
            '-',
            'text-gray-700 border-gray-200'
          )}
        </FieldBlock>

        <FieldBlock label="输出参数">
          {renderChipList(
            outputParameters,
            '-',
            'text-gray-700 border-gray-200'
          )}
        </FieldBlock>

        <FieldBlock label="过程类型">
          <span className="text-sm text-gray-700">{processTypeText}</span>
        </FieldBlock>

        <FieldBlock label="运行时配置">
          <span className="text-sm text-gray-700">{runtimeConfigText}</span>
        </FieldBlock>

        <FieldBlock label="">
          <div className="flex justify-center mt-2 mb-8">
            <Button type="primary" onClick={onOpenProcessDefinition}>
              编辑Process定义
            </Button>
          </div>
        </FieldBlock>
      </SectionBlock>
    </div>
  );
};

export default ProcessNodePropertyPanel;
